import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/i18n/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { useLocalizedRoute } from "@/hooks/useLocalizedRoute";
import { LAST_UPDATED, LEGAL_ENTITY } from "@/lib/legal";

const PrivacyPolicy = () => {
  const { t, lang } = useLanguage();
  useLocalizedRoute();

  useSEO({
    title: { es: t("seo", "privacyTitle"), en: t("seo", "privacyTitle") },
    description: { es: t("seo", "privacyDesc"), en: t("seo", "privacyDesc") },
    path: lang === "es" ? "/privacidad" : "/en/privacy-policy",
    pathEs: "/privacidad",
    pathEn: "/en/privacy-policy",
  });

  const es = lang === "es";
  const updated = es ? LAST_UPDATED.es : LAST_UPDATED.en;

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background pt-6 pb-20">
        <section className="container max-w-3xl">
          {es ? (
            <article className="prose prose-lg max-w-none text-foreground">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{updated}</p>
              <h1 className="font-display text-4xl font-bold mb-8">Política de Privacidad</h1>

              <h2>1. Responsable del tratamiento</h2>
              <ul>
                <li><strong>Responsable:</strong> {LEGAL_ENTITY.name} (marca comercial {LEGAL_ENTITY.brand})</li>
                <li><strong>Domicilio:</strong> {LEGAL_ENTITY.address}</li>
                <li><strong>Email de privacidad:</strong> {LEGAL_ENTITY.privacyEmail}</li>
                <li><strong>Web:</strong> retaillemon.com · panel.retaillemon.com</li>
              </ul>
              <p>
                Somos una sociedad estadounidense (Delaware) que presta servicios digitales B2B a empresas de cualquier
                país. Esta política se aplica por igual a todos nuestros clientes, con independencia de su lugar de
                establecimiento (América, Europa u otros mercados).
              </p>


              <h2>2. Transferencias internacionales de datos</h2>
              <p>
                Los datos se tratan y almacenan en <strong>Estados Unidos</strong> y en otros países en los que operan
                nuestros proveedores tecnológicos. Al utilizar la plataforma, el usuario entiende y acepta que sus
                datos se transfieren fuera de su país de residencia. Aplicamos garantías contractuales con nuestros
                proveedores (incluidas cláusulas contractuales tipo cuando resultan aplicables) y medidas técnicas de
                protección equivalentes.
              </p>

              <h2>3. Datos que tratamos</h2>
              <ul>
                <li><strong>Cuenta y perfil:</strong> nombre, email, idioma, contraseña cifrada o identificador del proveedor de inicio de sesión (Google/Apple), avatar.</li>
                <li><strong>Datos de negocio y onboarding:</strong> sector, rol, tamaño, retos, presencia digital.</li>
                <li><strong>Diagnósticos y locales:</strong> ubicaciones guardadas, fotografías del local, análisis de zona y resultados del test.</li>
                <li><strong>Datos económicos introducidos por el usuario:</strong> ventas, márgenes, costes y escenarios de las calculadoras.</li>
                <li><strong>Contenido propio:</strong> notas, checklists, fichas, publicaciones en la comunidad y archivos adjuntos.</li>
                <li><strong>Presupuestos y solicitudes:</strong> descripción de la necesidad, documentos y fotos de referencia.</li>
                <li><strong>Conversaciones con el asistente de IA y con soporte:</strong> mensajes, adjuntos y metadatos técnicos.</li>
                <li><strong>Facturación:</strong> datos fiscales de facturación e historial de pagos. Los datos de tarjeta los procesa <strong>Stripe</strong>; nosotros no los almacenamos.</li>
                <li><strong>Uso y técnica:</strong> métricas de actividad, dispositivo, navegador, IP y cookies.</li>
              </ul>

              <h2>4. Finalidades y bases del tratamiento</h2>
              <ul>
                <li><strong>Ejecución del contrato:</strong> dar acceso al panel, prestar la mentoría, gestionar presupuestos y emitir facturas.</li>
                <li><strong>Consentimiento:</strong> newsletter y comunicaciones comerciales, cookies no esenciales.</li>
                <li><strong>Interés legítimo:</strong> seguridad, prevención del fraude, soporte, estadísticas agregadas y mejora del producto.</li>
                <li><strong>Obligación legal:</strong> conservación contable y fiscal conforme a la legislación estadounidense.</li>
              </ul>

              <h2>5. Conversaciones con inteligencia artificial</h2>
              <p>
                El asistente de la plataforma funciona con modelos de IA de terceros. Los mensajes se envían al
                proveedor del modelo para generar la respuesta y se <strong>almacenan en nuestra base de datos</strong>
                para dar continuidad a la conversación, ofrecer soporte y mejorar la calidad del servicio. El equipo de
                Retail Lemon puede revisar conversaciones con esas finalidades y para entrenamiento interno del
                asistente.
              </p>
              <p>
                Recomendamos <strong>no introducir datos personales sensibles</strong> (salud, ideología, datos
                bancarios completos), datos de terceros sin base legítima ni información confidencial ajena. Las
                respuestas del asistente pueden contener errores y no constituyen asesoramiento profesional.
              </p>

              <h2>6. Encargados y proveedores</h2>
              <p>Trabajamos con proveedores que acceden a datos únicamente para prestarnos servicio:</p>
              <ul>
                <li><strong>Infraestructura, base de datos y funciones de servidor</strong> (alojamiento en la nube).</li>
                <li><strong>Stripe</strong> — procesamiento de pagos y facturación.</li>
                <li><strong>Google</strong> — inicio de sesión, agenda de sesiones y videollamada.</li>
                <li><strong>Proveedores de modelos de IA</strong> — generación de respuestas, informes y tips.</li>
                <li><strong>Proveedor de envío de email</strong> — emails transaccionales y newsletter.</li>
                <li><strong>Analítica</strong> — medición de uso agregada. Si aceptas las cookies de analítica y tienes cuenta, enviamos a Google Analytics un identificador anónimo aleatorio (nunca tu email ni tu ID de cuenta) junto con tu tipo de plan y sector, para entender el uso entre dispositivos; se elimina al cerrar sesión o al retirar el consentimiento.</li>
              </ul>
              <p>No vendemos ni alquilamos datos personales a terceros, ni los cedemos salvo obligación legal.</p>

              <h2>7. Conservación</h2>
              <ul>
                <li><strong>Cuenta y contenido del panel:</strong> mientras la cuenta esté activa y hasta 12 meses después de su cierre.</li>
                <li><strong>Conversaciones de IA y soporte:</strong> hasta 24 meses desde el último mensaje.</li>
                <li><strong>Presupuestos y documentos asociados:</strong> mientras exista relación contractual y durante los plazos de prescripción aplicables.</li>
                <li><strong>Datos de facturación:</strong> el plazo exigido por la normativa contable y fiscal estadounidense.</li>
                <li><strong>Leads y contactos sin contrato:</strong> máximo 24 meses desde la última interacción.</li>
              </ul>

              <h2>8. Derechos del usuario</h2>
              <p>
                Aunque no estamos establecidos en la Unión Europea, ofrecemos <strong>voluntariamente</strong> a todos
                nuestros clientes, con independencia de su país, los siguientes derechos: <strong>acceso,
                rectificación, supresión, oposición, limitación y portabilidad</strong>, así como la retirada del
                consentimiento para comunicaciones comerciales en cualquier momento.
              </p>
              <p>
                Este compromiso se aplica en términos equivalentes a las normativas locales de nuestros clientes, entre
                otras: RGPD (UE), UK GDPR, Ley 25.326 (Argentina), Ley 1581 de 2012 (Colombia), LFPDPPP (México), LGPD
                (Brasil) y CCPA/CPRA (California). <strong>No vendemos datos personales</strong> ni realizamos
                decisiones automatizadas con efectos jurídicos sobre el usuario.
              </p>
              <p>
                Para ejercer cualquier derecho, escribe a{" "}
                <a href={`mailto:${LEGAL_ENTITY.privacyEmail}`} className="text-foreground">{LEGAL_ENTITY.privacyEmail}</a>{" "}
                indicando "Protección de Datos" en el asunto. Responderemos en un plazo máximo de 30 días. Si no estás
                conforme con nuestra respuesta, puedes dirigirte a la autoridad de protección de datos de tu país de
                residencia.
              </p>

              <h2>9. Seguridad</h2>
              <p>
                Aplicamos medidas técnicas y organizativas razonables: cifrado en tránsito, control de acceso por
                usuario mediante políticas de seguridad a nivel de fila, registro de accesos administrativos y
                minimización de datos. Ningún sistema es infalible, por lo que el usuario debe custodiar sus
                credenciales y mantener sus propias copias del contenido relevante.
              </p>

              <h2>10. Menores</h2>
              <p>
                La plataforma es un servicio profesional B2B y no está dirigida a menores de 18 años. No recopilamos
                intencionadamente datos de menores.
              </p>

              <h2>11. Cambios en esta política</h2>
              <p>
                Podemos actualizar esta política por motivos legales o de servicio. Notificaremos los cambios
                sustanciales por email o desde el panel.
              </p>
            </article>
          ) : (
            <article className="prose prose-lg max-w-none text-foreground">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{updated}</p>
              <h1 className="font-display text-4xl font-bold mb-8">Privacy Policy</h1>

              <h2>1. Data controller</h2>
              <ul>
                <li><strong>Controller:</strong> {LEGAL_ENTITY.name} (trading as {LEGAL_ENTITY.brand})</li>
                <li><strong>Address:</strong> {LEGAL_ENTITY.address}</li>
                <li><strong>Privacy email:</strong> {LEGAL_ENTITY.privacyEmail}</li>
                <li><strong>Websites:</strong> retaillemon.com · panel.retaillemon.com</li>
              </ul>
              <p>
                We are a U.S. company (Delaware) providing B2B digital services to businesses in any country. This
                policy applies equally to all of our clients, wherever they are established (the Americas, Europe or
                other markets).
              </p>


              <h2>2. International data transfers</h2>
              <p>
                Data is processed and stored in the <strong>United States</strong> and in other countries where our
                technology providers operate. By using the platform you understand and accept that your data is
                transferred outside your country of residence. We apply contractual safeguards with our providers
                (including standard contractual clauses where applicable) and equivalent technical protection measures.
              </p>

              <h2>3. Data we process</h2>
              <ul>
                <li><strong>Account and profile:</strong> name, email, language, hashed password or SSO identifier (Google/Apple), avatar.</li>
                <li><strong>Business and onboarding data:</strong> sector, role, size, challenges, digital presence.</li>
                <li><strong>Diagnostics and locations:</strong> saved locations, store photos, area analysis and quiz results.</li>
                <li><strong>Financial inputs:</strong> sales, margins, costs and scenarios entered in the calculators.</li>
                <li><strong>Your own content:</strong> notes, checklists, cards, community posts and attachments.</li>
                <li><strong>Quotes and requests:</strong> description of the need, documents and reference photos.</li>
                <li><strong>AI assistant and support conversations:</strong> messages, attachments and technical metadata.</li>
                <li><strong>Billing:</strong> invoicing tax details and payment history. Card data is processed by <strong>Stripe</strong>; we never store it.</li>
                <li><strong>Usage and technical data:</strong> activity metrics, device, browser, IP and cookies.</li>
              </ul>

              <h2>4. Purposes and legal grounds</h2>
              <ul>
                <li><strong>Contract performance:</strong> providing panel access, delivering mentoring, managing quotes and issuing invoices.</li>
                <li><strong>Consent:</strong> newsletter and marketing communications, non-essential cookies.</li>
                <li><strong>Legitimate interest:</strong> security, fraud prevention, support, aggregated statistics and product improvement.</li>
                <li><strong>Legal obligation:</strong> accounting and tax retention under U.S. law.</li>
              </ul>

              <h2>5. AI conversations</h2>
              <p>
                The platform assistant runs on third-party AI models. Messages are sent to the model provider to
                generate the answer and are <strong>stored in our database</strong> to keep conversation continuity,
                provide support and improve service quality. The Retail Lemon team may review conversations for those
                purposes and for internal assistant training.
              </p>
              <p>
                We recommend <strong>not entering sensitive personal data</strong> (health, beliefs, full banking
                details), third-party data without a lawful basis, or confidential information belonging to others.
                Assistant answers may contain errors and do not constitute professional advice.
              </p>

              <h2>6. Processors and providers</h2>
              <p>We work with providers that access data solely to serve us:</p>
              <ul>
                <li><strong>Cloud infrastructure, database and server functions.</strong></li>
                <li><strong>Stripe</strong> — payment processing and billing.</li>
                <li><strong>Google</strong> — sign-in, session scheduling and video calls.</li>
                <li><strong>AI model providers</strong> — generating answers, reports and tips.</li>
                <li><strong>Email delivery provider</strong> — transactional emails and newsletter.</li>
                <li><strong>Analytics</strong> — aggregated usage measurement. If you accept analytics cookies and have an account, we send Google Analytics a random anonymous identifier (never your email or account ID) together with your plan type and sector, to understand usage across devices; it is removed when you sign out or withdraw consent.</li>
              </ul>
              <p>We do not sell or rent personal data, nor disclose it except where legally required.</p>

              <h2>7. Retention</h2>
              <ul>
                <li><strong>Account and panel content:</strong> while the account is active and up to 12 months after closure.</li>
                <li><strong>AI and support conversations:</strong> up to 24 months from the last message.</li>
                <li><strong>Quotes and related documents:</strong> for the duration of the contractual relationship and applicable limitation periods.</li>
                <li><strong>Billing data:</strong> the period required by U.S. accounting and tax rules.</li>
                <li><strong>Leads without a contract:</strong> maximum 24 months from the last interaction.</li>
              </ul>

              <h2>8. Your rights</h2>
              <p>
                Although we are not established in the European Union, we <strong>voluntarily</strong> offer all clients,
                regardless of country, the following rights: <strong>access, rectification, erasure, objection,
                restriction and portability</strong>, as well as withdrawal of consent for marketing at any time.
              </p>
              <p>
                This commitment applies on equivalent terms to our clients' local regulations, including: GDPR (EU), UK
                GDPR, Law 25.326 (Argentina), Law 1581/2012 (Colombia), LFPDPPP (Mexico), LGPD (Brazil) and CCPA/CPRA
                (California). <strong>We do not sell personal data</strong> and we do not carry out automated
                decision-making with legal effects on users.
              </p>
              <p>
                To exercise any right, email{" "}
                <a href={`mailto:${LEGAL_ENTITY.privacyEmail}`} className="text-foreground">{LEGAL_ENTITY.privacyEmail}</a>{" "}
                with "Data Protection" in the subject line. We reply within 30 days at most. If you are not satisfied
                with our response, you may contact the data protection authority of your country of residence.
              </p>

              <h2>9. Security</h2>
              <p>
                We apply reasonable technical and organizational measures: encryption in transit, per-user access
                control through row-level security policies, administrative access logging and data minimization. No
                system is infallible, so you should safeguard your credentials and keep your own copies of relevant
                content.
              </p>

              <h2>10. Minors</h2>
              <p>
                The platform is a professional B2B service and is not directed at anyone under 18. We do not knowingly
                collect data from minors.
              </p>

              <h2>11. Changes to this policy</h2>
              <p>
                We may update this policy for legal or service reasons. Material changes will be notified by email or
                through the panel.
              </p>
            </article>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
